import type { History } from 'history';
import { Action, Dispatch, Middleware, MiddlewareAPI } from 'redux';
import {
  HISTORY_UPDATE,
  HistoryAction,
  PayloadParams,
  GoParams,
  NavigationParams,
  HISTORY_METHOD_PUSHSTATE,
  HISTORY_METHOD_REPLACESTATE,
  HISTORY_METHOD_GO,
  HISTORY_METHOD_GO_FORWARD,
  HISTORY_METHOD_GO_BACK
} from 'store/history/actions';
import { historyLocationState } from './historyLocationState';

interface NavigateMiddlewareType {
  [key: string]: (action: NavigationParams) => void;
}

interface ForceAllowBackProps {
  forceAllowBack?: boolean;
}

const isForceAllowBack = (state: unknown): state is ForceAllowBackProps => {
  if (!state) {
    return false;
  }
  return (state as ForceAllowBackProps).forceAllowBack !== undefined;
};

// TODO: save the page in state when all sites are written to react
const navigateMiddleware = (history: History): NavigateMiddlewareType => ({
  [HISTORY_METHOD_PUSHSTATE]: ({ url, scrollPosition, screenHeight, state }: PayloadParams) => {
    const newStateOfCurrentPage = {
      ...historyLocationState(history),
      scrollTo: scrollPosition,
      screenHeight
    };
    const forceAllowBack = isForceAllowBack(state) && state.forceAllowBack;
    const path = url.toPath();
    if (history.location.pathname === url.pathname && !forceAllowBack) {
      history.replace(path, newStateOfCurrentPage);
    } else {
      history.replace(history.createHref(history.location), newStateOfCurrentPage);
      history.push(path);
    }
  },
  [HISTORY_METHOD_REPLACESTATE]: ({ url }: PayloadParams) => {
    const newState = {
      scrollTo: historyLocationState(history)?.scrollTo
    };
    history.replace(url.toPath(), newState);
  },
  [HISTORY_METHOD_GO]: (go: GoParams) => {
    history.go(go);
  },
  [HISTORY_METHOD_GO_FORWARD]: () => {
    history.forward();
  },
  [HISTORY_METHOD_GO_BACK]: () => {
    history.back();
  }
});

const isHistoryAction = <A>(action: A | HistoryAction): action is HistoryAction =>
  (action as HistoryAction).type === HISTORY_UPDATE;

export const middleware =
  (history: History): Middleware =>
  (_: MiddlewareAPI) => {
    const middleware = navigateMiddleware(history);
    return (next: Dispatch) =>
      <A extends Action>(action: A | HistoryAction) => {
        if (!isHistoryAction(action)) {
          return next(action);
        }
        return middleware[action.method](action.payload);
      };
  };
